import { useMemo } from 'react';
import Forward from '../../assets/icons/Forward';
import { useAppearance } from '../../hooks/useAppearance';
import { useConnectWithWallet } from '../../hooks/useConnectWithWallet';
import { useLastConnectorId } from '../../hooks/useLastConnectorId';
import useLocales from '../../hooks/useLocales';
import { useRouter } from '../../hooks/useRouter';
import useWalletsProps from '../../hooks/useWalletsProps';
import { RoutePath } from '../../pages';
import Button from '../button';
import { ForwardContainer, WalletIconContainer, WalletItem, WalletLable, WalletsContainer } from './styles';
import type { WalletData } from './types';

const Wallets = ({ expandList }: { expandList?: boolean }) => {
  const { navigate } = useRouter();
  const wallets = useWalletsProps();
  const locales = useLocales();
  const { lastConnectorId } = useLastConnectorId();
  const connectWallet = useConnectWithWallet();
  const { appearance } = useAppearance();

  const walletData = useMemo(() => {
    const _wallets = wallets.filter(
      (wallet) => wallet.connector.type !== 'particleAuth' && wallet.connector.id !== 'passkeySmartWallet'
    );
    // merge wallet
    const datas: WalletData[] = [];
    for (const wallet of _wallets) {
      const key = wallet.groupId || wallet.connector.id;
      const itemData = datas.find((w) => w.key === key);
      if (itemData) {
        itemData.props.push(wallet);
      } else {
        datas.push({
          key,
          props: [wallet],
          rankingWeight: 0,
        });
      }
    }

    // set lable
    const recommendedWallets = appearance?.recommendedWallets || [];
    datas.forEach((data) => {
      const ids = data.key.split(',').map((id) => id.trim());
      // multichain lable
      if (data.props.length > 1) {
        data.lable = locales.multichain;
      }

      // installed lable
      if (data.props.some((item) => item.isInstalled)) {
        data.lable = locales.installed;
        data.rankingWeight = 100;
      }

      // custom lable
      const recommended = recommendedWallets.find((item) => ids.includes(item.walletId));
      if (recommended) {
        if (recommended.label !== 'none') {
          if (recommended.label?.toLowerCase() === 'recommended') {
            data.lable = locales.recommended;
          } else if (recommended.label?.toLowerCase() === 'popular') {
            data.lable = locales.popular;
          } else {
            data.lable = recommended.label || locales.recommended;
          }
          data.customLable = true;
        } else {
          data.customLable = undefined;
        }
        const recommendedIndex = recommendedWallets.indexOf(recommended);
        data.rankingWeight = 200 + (99 - recommendedIndex);
      }

      // last used lable
      if (lastConnectorId && ids.includes(lastConnectorId)) {
        data.lable = locales.lastUsed;
        data.rankingWeight = 500;
        data.customLable = undefined;
      }
    });

    // sort items
    datas.sort((a, b) => {
      if (a.rankingWeight > b.rankingWeight) {
        return -1;
      }

      if (a.rankingWeight < b.rankingWeight) {
        return 1;
      }

      return 0;
    });

    return datas;
  }, [locales, wallets, appearance?.recommendedWallets, lastConnectorId]);

  const renderWallets = useMemo(() => {
    if (expandList) {
      return walletData;
    }

    if (appearance?.collapseWalletList) {
      return [];
    }

    if (walletData.length > 3) {
      return walletData.slice(0, 3);
    }

    return walletData;
  }, [walletData, expandList, appearance?.collapseWalletList]);

  const displayActionBtn = useMemo(() => {
    return !expandList && (appearance?.collapseWalletList || walletData.length > 3);
  }, [expandList, appearance?.collapseWalletList, walletData]);

  const onWalletClick = (data: WalletData) => {
    if (data.props.length > 1) {
      // route to select chain
      navigate(RoutePath.SelectChain, { walletData: data });
    } else {
      const walletProps = data.props[0];
      connectWallet(walletProps);
    }
  };

  return (
    <WalletsContainer>
      {renderWallets.map((walletData) => {
        return (
          <WalletItem key={walletData.key} onClick={() => onWalletClick(walletData)}>
            <WalletIconContainer>{walletData.props[0].icon}</WalletIconContainer>
            {walletData.props[0].name}
            {walletData.lable && <WalletLable $customLable={walletData.customLable}>{walletData.lable}</WalletLable>}
            <ForwardContainer>
              <Forward />
            </ForwardContainer>
          </WalletItem>
        );
      })}

      {displayActionBtn && (
        <Button
          onClick={() => {
            navigate(RoutePath.Connectors);
          }}
          color='secondary'
        >
          {appearance?.collapseWalletList ? locales.continueWithWallet : locales.viewAllWallets}
        </Button>
      )}
    </WalletsContainer>
  );
};

export default Wallets;
